import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const UnderstandingGeneticHighCholesterol = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const contentTypes = [
    {
      id: 'blog',
      title: 'Blog',
      icon: 'ri-article-line',
      description: 'In-depth articles'
    },
    {
      id: 'videos',
      title: 'Videos',
      icon: 'ri-video-line',
      description: 'Educational videos'
    },
    {
      id: 'infograph',
      title: 'Infograph',
      icon: 'ri-bar-chart-box-line',
      description: 'Visual guides'
    }
  ];

  const blogContent = [
    {
      title: "Living with FH: A Patient's Journey",
      excerpt: "Discover how early diagnosis and proper treatment transformed one family's approach to managing genetic high cholesterol.",
      date: "Dec 15, 2024",
      readTime: "5 min read",
      image: "https://public.readdy.ai/ai/img_res/fce2bebde8ee728d63badda5fe1d96d7.jpg"
    },
    {
      title: "New Advances in FH Treatment",
      excerpt: "Recent breakthroughs in medication and therapy options are giving patients more effective ways to manage their cholesterol levels.",
      date: "Dec 10, 2024",
      readTime: "7 min read",
      image: "https://readdy.ai/api/search-image?query=modern%20medical%20research%20laboratory%20with%20scientists%20working%20soft%20lighting%20clean%20aesthetic%20coral%20and%20cream%20color%20palette&width=400&height=250&seq=blog2&orientation=landscape"
    },
    {
      title: "Family Screening: Why It Matters",
      excerpt: "When one family member is diagnosed with FH, screening the entire family can save lives through early intervention.",
      date: "Dec 5, 2024",
      readTime: "4 min read",
      image: "https://readdy.ai/api/search-image?query=multigenerational%20family%20having%20conversation%20in%20living%20room%20warm%20atmosphere%20soft%20natural%20lighting%20cream%20and%20coral%20tones&width=400&height=250&seq=blog3&orientation=landscape"
    }
  ];

  const videoContent = [
    {
      title: "Understanding FH: Expert Explains",
      duration: "12:45",
      views: "24K views",
      thumbnail: "https://readdy.ai/api/search-image?query=doctor%20in%20white%20coat%20explaining%20medical%20concept%20in%20modern%20office%20professional%20setting%20soft%20lighting%20coral%20and%20cream%20color%20tones&width=400&height=250&seq=video1&orientation=landscape"
    },
    {
      title: "Patient Story: My FH Diagnosis",
      duration: "8:30",
      views: "18K views",
      thumbnail: "https://readdy.ai/api/search-image?query=person%20sharing%20personal%20story%20in%20comfortable%20interview%20setting%20warm%20lighting%20soft%20coral%20and%20cream%20background&width=400&height=250&seq=video2&orientation=landscape"
    },
    {
      title: "Lifestyle Tips for Managing FH",
      duration: "15:20",
      views: "32K views",
      thumbnail: "https://readdy.ai/api/search-image?query=healthy%20lifestyle%20concept%20with%20exercise%20and%20nutrition%20elements%20bright%20cheerful%20atmosphere%20coral%20and%20cream%20color%20palette&width=400&height=250&seq=video3&orientation=landscape"
    }
  ];

  const infographContent = [
    {
      title: "FH by the Numbers",
      description: "Key statistics about Familial Hypercholesterolemia prevalence and impact worldwide.",
      image: "https://readdy.ai/api/search-image?query=clean%20modern%20infographic%20design%20with%20charts%20and%20statistics%20medical%20data%20visualization%20coral%20and%20cream%20color%20scheme%20minimalist%20style&width=400&height=300&seq=info1&orientation=landscape"
    },
    {
      title: "The FH Inheritance Pattern",
      description: "Visual guide showing how FH is passed from parents to children through genetics.",
      image: "https://readdy.ai/api/search-image?query=family%20tree%20diagram%20showing%20genetic%20inheritance%20pattern%20clean%20modern%20design%20coral%20and%20cream%20colors%20medical%20illustration&width=400&height=300&seq=info2&orientation=landscape"
    },
    {
      title: "Treatment Timeline",
      description: "Step-by-step visual guide from diagnosis to successful FH management.",
      image: "https://readdy.ai/api/search-image?query=timeline%20infographic%20showing%20medical%20treatment%20journey%20clean%20modern%20design%20coral%20and%20cream%20color%20palette%20healthcare%20visualization&width=400&height=300&seq=info3&orientation=landscape"
    }
  ];

  const testimonials = [
    {
      quote: "After my diagnosis, I felt overwhelmed. But understanding that FH is manageable with the right treatment gave me hope. My cholesterol levels are now under control, and I feel healthier than ever.",
      name: "Sarah Mitchell",
      info: "Diagnosed 2019",
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20a%20middle%20aged%20woman%20with%20warm%20smile%20soft%20lighting%20neutral%20background%20high%20quality%20photography&width=80&height=80&seq=1&orientation=squarish"
    },
    {
      quote: "Getting my family screened after my diagnosis was the best decision. We caught my daughter early, and she is now on preventive treatment. Knowledge truly is power when it comes to genetic conditions.",
      name: "Michael Chen",
      info: "Diagnosed 2021",
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20an%20asian%20man%20in%20his%2050s%20friendly%20expression%20soft%20lighting%20neutral%20background%20high%20quality%20photography&width=80&height=80&seq=2&orientation=squarish"
    },
    {
      quote: "Living with FH has taught me the importance of proactive health management. Regular monitoring and medication have become part of my routine, and I am living a full, active life.",
      name: "Emma Rodriguez",
      info: "Diagnosed 2018",
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20a%20latina%20woman%20in%20her%2040s%20confident%20smile%20soft%20lighting%20neutral%20background%20high%20quality%20photography&width=80&height=80&seq=3&orientation=squarish"
    }
  ];

  const keyInfoCards = [
    {
      title: "What is Familial Hypercholesterolemia?",
      description: "FH is an inherited condition causing extremely high LDL cholesterol levels from birth, significantly increasing heart disease risk.",
      image: "https://readdy.ai/api/search-image?query=abstract%20DNA%20double%20helix%20structure%20with%20soft%20coral%20and%20cream%20colors%20medical%20scientific%20illustration%20clean%20minimal%20background%20modern%20healthcare%20aesthetic&width=400&height=240&seq=4&orientation=landscape"
    },
    {
      title: "Symptoms & Warning Signs",
      description: "Often silent, but may include cholesterol deposits around eyes, tendons, or early heart disease in family members.",
      image: "https://readdy.ai/api/search-image?query=medical%20illustration%20of%20heart%20health%20monitoring%20with%20soft%20warm%20colors%20coral%20and%20cream%20tones%20clean%20minimal%20healthcare%20aesthetic%20professional&width=400&height=240&seq=5&orientation=landscape"
    },
    {
      title: "Diagnosis & Testing",
      description: "Diagnosed through lipid panels, genetic testing, and family history assessment. Early detection is crucial for prevention.",
      image: "https://readdy.ai/api/search-image?query=medical%20laboratory%20blood%20test%20tubes%20and%20diagnostic%20equipment%20soft%20coral%20and%20cream%20color%20palette%20clean%20minimal%20healthcare%20aesthetic%20professional&width=400&height=240&seq=6&orientation=landscape"
    },
    {
      title: "Treatment Options",
      description: "Includes statins, lifestyle modifications, and advanced therapies. With proper treatment, risks can be significantly reduced.",
      image: "https://readdy.ai/api/search-image?query=modern%20medical%20treatment%20concept%20with%20medication%20and%20healthy%20lifestyle%20elements%20soft%20coral%20and%20cream%20colors%20clean%20minimal%20healthcare%20aesthetic&width=400&height=240&seq=7&orientation=landscape"
    }
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Genetic Health Education Series
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Understanding Genetic High Cholesterol
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Familial Hypercholesterolemia (FH) is a genetic condition that causes dangerously high cholesterol levels. Learn about its causes, symptoms, and how early detection can save lives.
            </p>
          </div>

          {/* Content Type Containers */}
          <div className="grid grid-cols-3 gap-6">
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog' 
                  ? 'ring-2 ring-[#f04e58] shadow-lg' 
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=person%20writing%20medical%20blog%20article%20on%20laptop%20computer%2C%20healthcare%20content%20creation%2C%20modern%20workspace%20with%20medical%20books%20and%20stethoscope%2C%20warm%20natural%20lighting%2C%20professional%20photography%2C%20clean%20desk%20setup&width=400&height=300&seq=blog-writing-001&orientation=landscape"
                  alt="Blog articles"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                  activeContentType === 'blog' 
                    ? 'bg-[#f04e58]' 
                    : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                }`}>
                  <i className={`ri-article-line text-xl transition-colors ${
                    activeContentType === 'blog' 
                      ? 'text-white' 
                      : 'text-[#f04e58] group-hover:text-white'
                  }`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth articles about FH</p>
                <div className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  activeContentType === 'blog' 
                    ? 'bg-[#f04e58] rotate-45' 
                    : 'bg-white/90 backdrop-blur-sm'
                }`}>
                  <i className={`ri-add-line text-lg ${
                    activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                  }`}></i>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos' 
                  ? 'ring-2 ring-[#f04e58] shadow-lg' 
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=medical%20education%20video%20recording%20setup%2C%20doctor%20presenting%20to%20camera%20in%20modern%20clinic%2C%20professional%20video%20production%20equipment%2C%20bright%20studio%20lighting%2C%20healthcare%20content%20creation%2C%20clean%20background&width=400&height=300&seq=video-recording-001&orientation=landscape"
                  alt="Educational videos"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                  activeContentType === 'videos' 
                    ? 'bg-[#f04e58]' 
                    : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                }`}>
                  <i className={`ri-video-line text-xl transition-colors ${
                    activeContentType === 'videos' 
                      ? 'text-white' 
                      : 'text-[#f04e58] group-hover:text-white'
                  }`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Videos</h3>
                <p className="text-gray-500 text-sm">Educational video content</p>
                <div className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  activeContentType === 'videos' 
                    ? 'bg-[#f04e58] rotate-45' 
                    : 'bg-white/90 backdrop-blur-sm'
                }`}>
                  <i className={`ri-add-line text-lg ${
                    activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                  }`}></i>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph' 
                  ? 'ring-2 ring-[#f04e58] shadow-lg' 
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=colorful%20medical%20infographic%20design%20on%20tablet%20screen%2C%20healthcare%20data%20visualization%2C%20charts%20and%20diagrams%20about%20heart%20health%2C%20modern%20digital%20illustration%2C%20clean%20professional%20layout%2C%20teal%20and%20coral%20colors&width=400&height=300&seq=infographic-design-001&orientation=landscape"
                  alt="Infographics"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                  activeContentType === 'infograph' 
                    ? 'bg-[#f04e58]' 
                    : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                }`}>
                  <i className={`ri-bar-chart-box-line text-xl transition-colors ${
                    activeContentType === 'infograph' 
                      ? 'text-white' 
                      : 'text-[#f04e58] group-hover:text-white'
                  }`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Visual guides and data</p>
                <div className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  activeContentType === 'infograph' 
                    ? 'bg-[#f04e58] rotate-45' 
                    : 'bg-white/90 backdrop-blur-sm'
                }`}>
                  <i className={`ri-add-line text-lg ${
                    activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                  }`}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section - Shows when Blog is clicked */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                5 Surprising Truths About the Genetic High Cholesterol You're Born With
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Dec 15, 2024
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  8 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=medical%20professional%20examining%20genetic%20test%20results%20and%20cholesterol%20charts%20in%20modern%20clinic%20office%2C%20healthcare%20consultation%20setting%2C%20professional%20medical%20photography%2C%20soft%20natural%20lighting%2C%20clean%20white%20and%20coral%20color%20tones&width=800&height=400&seq=blog-hero-genetic-cholesterol&orientation=landscape"
                  alt="Genetic High Cholesterol"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Introduction: The Hidden Cause of High Cholesterol</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When we think of high cholesterol, we often picture a condition caused by lifestyle choices—a poor diet, too little exercise—that develops later in life. While these factors are certainly important, they don't tell the whole story. For many people, dangerously high cholesterol isn't something they acquire; it's something they're born with.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This inherited condition is called <strong>Familial Heterozygous Hypercholesterolemia (FeFH)</strong>, and it works silently, often going undetected for decades. It challenges the common belief that high cholesterol is solely a consequence of personal habits. This article will cover the five most surprising and important facts you need to know about this hidden genetic condition.
                </p>
              </div>

              {/* Takeaway 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    You Can Have Dangerously High Cholesterol From Birth—And Not Know It
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Familial Heterozygous Hypercholesterolemia (FeFH) causes high LDL cholesterol from the moment you are born. The condition often has no early symptoms, and many people with FeFH look and feel perfectly healthy.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Because of this, the only reliable clue for most people is a routine blood test that reveals unusually high levels of "bad" cholesterol. While the disease is usually silent, some individuals may develop physical signs like small cholesterol bumps on their tendons (xanthomas), yellow patches near the eyes (xanthelasmas), or a grey-white ring around the cornea.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
                  <p className="text-gray-900 font-semibold mb-2">Key Numbers:</p>
                  <p className="text-gray-700 leading-relaxed">
                    Specifically, the LDL cholesterol level for someone with FeFH is typically between <strong>190–400 mg/dL</strong>. This is two to three times higher than the normal level of less than 130 mg/dL.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mt-4">
                  This fact is so important because it reframes our understanding of high cholesterol; it's not always an acquired condition of adulthood. The danger lies in its silence, allowing damage to accumulate for years before it's ever discovered.
                </p>
              </div>

              {/* Takeaway 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Problem Is Your Liver's "Filtering System" is at Half-Strength
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The underlying cause of FeFH is a simple but significant genetic fault. A person with this condition has inherited a single faulty gene responsible for clearing cholesterol from the blood. As a result, their liver produces only half as many working LDL receptors as it should.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These receptors act like a filtering system, pulling "bad" LDL cholesterol out of the bloodstream. When you only have half the necessary filters, your liver can't keep up. LDL cholesterol builds up in the blood over time, causing arteries to stiffen and clog earlier than usual, sometimes decades ahead of schedule.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
                  <p className="text-gray-900 font-semibold mb-2">Important Note:</p>
                  <p className="text-gray-700 leading-relaxed">
                    This mechanical flaw explains why diet and exercise alone are often not enough to control this type of high cholesterol.
                  </p>
                </div>
              </div>

              {/* Takeaway 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's a 50/50 Genetic Coin Toss
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The way FeFH is passed down through families is remarkably straightforward. If one of your parents has the condition, you have a <strong>50% chance</strong> of inheriting the faulty gene yourself. This inheritance pattern is known medically as autosomal dominant, meaning only one copy of the gene is needed to cause the condition.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#f04e58]">50%</span>
                      </div>
                      <p className="text-sm text-gray-600">Inherit FeFH</p>
                    </div>
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#0d9488]">50%</span>
                      </div>
                      <p className="text-sm text-gray-600">Don't Inherit</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  This has profound implications for your entire family. Knowing your own status is critical for understanding your children's potential risk, and knowing your parents' health history is a crucial clue for your own health. It underscores the vital importance of discussing health history with relatives and pursuing family screening if FeFH is identified.
                </p>
              </div>

              {/* Takeaway 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It Can Lead to Heart Attacks Decades Earlier Than Normal
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The lifelong burden of extremely high LDL cholesterol is not a minor health issue; it is a major risk factor for premature cardiovascular disease. The constant plaque buildup in the arteries from birth significantly accelerates the timeline for serious cardiac events.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58] mb-4">
                  <p className="text-gray-900 font-semibold mb-3">Timeline of Risk:</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <i className="ri-user-line text-[#f04e58]"></i>
                      <p className="text-gray-700"><strong>Men:</strong> Heart attack or angina by age 40–50</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-user-line text-[#f04e58]"></i>
                      <p className="text-gray-700"><strong>Women:</strong> Heart disease by age 50–60</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Tragically, many people only discover they have the condition after suffering a major and life-altering cardiac event. This highlights the absolute urgency of early detection and proactive treatment to prevent a preventable outcome.
                </p>
              </div>

              {/* Takeaway 5 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    5
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Outlook is Excellent—If You Get Diagnosed and Treated
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Despite the serious risks, the prognosis for individuals with FeFH is incredibly positive when the condition is identified and managed. FeFH is highly treatable, and early intervention can completely change its course.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Treatment is a multi-faceted approach involving medication, lifestyle adjustments, and screening family members.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-capsule-line text-[#f04e58]"></i>
                        Medications
                      </h4>
                      <p className="text-gray-700 leading-relaxed text-sm">
                        Modern therapies are highly effective. These include <strong>Statins</strong>, which reduce cholesterol production; <strong>Ezetimibe</strong>, which blocks cholesterol absorption; and <strong>PCSK9 inhibitors</strong>, which help the liver remove more LDL from the blood.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-heart-pulse-line text-[#0d9488]"></i>
                        Lifestyle
                      </h4>
                      <p className="text-gray-700 leading-relaxed text-sm">
                        A heart-healthy lifestyle remains crucial. This includes eating a low-fat, high-fiber diet, getting regular exercise, and avoiding smoking.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-group-line text-[#f04e58]"></i>
                        Family Screening
                      </h4>
                      <p className="text-gray-700 leading-relaxed text-sm">
                        Because FeFH is genetic, testing parents, siblings, and children of an affected individual is a critical step in preventing the disease's impact on the next generation.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white">
                  <p className="font-semibold mb-2">Excellent Prognosis:</p>
                  <p className="text-white/90 leading-relaxed">
                    With proper treatment, LDL cholesterol levels can drop by <strong>50–70%</strong>, and individuals can expect to have a <strong>normal life expectancy</strong>. This means the premature heart attacks described earlier are not a foregone conclusion, but a preventable outcome. Knowledge and early diagnosis are the keys to transforming a dangerous genetic risk into a completely manageable condition.
                  </p>
                </div>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#f04e58]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Conclusion: What Does Your Family Health History Tell You?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Familial Heterozygous Hypercholesterolemia is a common, serious, but ultimately treatable genetic condition that often hides in plain sight within families. It proves that high cholesterol is not always a reflection of lifestyle, but can be a matter of genetics.
                </p>
                <p className="text-gray-700 leading-relaxed font-medium">
                  Knowing that a simple blood test and a conversation about family history could prevent a heart attack decades from now, isn't it time to find out what your numbers are?
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Take Action Today</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  If you have a family history of high cholesterol or early heart disease, don't wait. Early detection and treatment can dramatically reduce your risk and help you live a long, healthy life.
                </p>
                <Link 
                  to="/"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Screening
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section - Shows when Videos is clicked */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Understanding Familial Hypercholesterolemia
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this comprehensive video to learn about genetic high cholesterol, its causes, symptoms, and treatment options.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-black aspect-video mb-8">
              <video
                controls
                autoPlay
                className="w-full h-full"
                poster="https://readdy.ai/api/search-image?query=medical%20education%20video%20thumbnail%20about%20cholesterol%20and%20heart%20health%2C%20professional%20healthcare%20presentation%2C%20doctor%20explaining%20genetic%20conditions%2C%20clean%20modern%20design%20with%20teal%20and%20coral%20accents&width=800&height=450&seq=video-poster-001&orientation=landscape"
              >
                <source 
                  src="https://rr5---sn-o097znz7.googlevideo.com/videoplayback?expire=1771280939&ei=C36TaYvfPMq8n88PprGo0Qo&ip=76.133.210.109&id=163d5ffaf8434c76&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&met=1771273739,&mh=AU&mm=32&mn=sn-o097znz7&ms=su&mv=m&mvi=5&pl=23&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=394.716&lmt=1769282245506123&mt=1771273282&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRQIgJpt126XSJk0JUZ2C4xmgdMJeXX6ZjorMIYYnUGvtjEoCIQDrdZ7GtOEBayXrJy5--dW1WQu_gU-7wKJWW1ZXlsTq0g==&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRAIgDRNDyIK86PJlBarOQ43oV0JcVxdzhVjjJPIxGk2gu_ICIDECfYNB-NfDTk_Zb7PmVzuoy8TMepPHCRnstxrUbZq9" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">FH Educational Series</h3>
                  <p className="text-sm text-gray-500">Duration: 6:34</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This video covers the essential information about Familial Hypercholesterolemia (FH), including how it's inherited, why early detection matters, and the treatment options available to manage this genetic condition effectively.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">More Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div key={index} className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group">
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section - Shows when Infograph is clicked */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                FeFH: Understanding Your Genetic High Cholesterol
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A comprehensive visual guide to Familial Heterozygous Hypercholesterolemia - from genetics and biology to treatment and outcomes.
              </p>
            </div>

            {/* Main Infographic Image */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg">
              <div className="w-full rounded-xl overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/aa01779462e56b3c437523049ab12eb9.png"
                  alt="FeFH Understanding Your Genetic High Cholesterol Infographic"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* Key Highlights */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-dna-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">The Biology of FeFH</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  50% inheritance risk if one parent has FH. The liver produces only half the normal LDL receptors needed.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-bar-chart-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">LDL Levels Comparison</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Normal: &lt;130 mg/dL | FeFH: 190-400 mg/dL (2-3x higher than normal levels).
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-heart-pulse-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Success</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  With proper treatment, LDL can drop 50-70%, allowing for a normal life expectancy.
                </p>
              </div>
            </div>

            {/* Download/Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a 
                href="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/aa01779462e56b3c437523049ab12eb9.png"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </a>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText('https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/aa01779462e56b3c437523049ab12eb9.png');
                }}
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default UnderstandingGeneticHighCholesterol;
